import React, { useState, useCallback, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { globalHistory as history } from '@reach/router'
import { parse } from 'query-string'
import trackGTM from 'utils/trackGTM'
import GA4 from 'utils/GA4'

import Button from 'components/Button'
import ChevronIcon from 'components/Icons/ChevronIcon'
import { Body12 } from 'components/Typography'
import { Container, Spacing50 } from 'styles/sharedStyle'
import arrayUtils from 'utils/array'
import { useTheme } from 'styled-components'

import SectionHeader from 'components/SectionHeader'
import BrandList from './BrandList'
import QuickNav from './QuickNav'

import {
  SearchButton,
  SearchButtonIcon,
  Section,
  Select,
  SelectIcon,
  ResultCount,
  ResultCountWrapper,
} from './BrandDirectory.style'

const BrandDirectory = ({
  bodyCopy,
  brands,
  villageName,
  header,
  loadMoreCTACopy,
  resultLabel,
  resultsLabel,
  categorySearchLabel,
  ctaLabel,
  visitBoutiqueLabel,
  toggleBrandSearch,
  servicesDropdownLabel,
  services,
  whatsappIcon,
  whatsappLabel,
  productCatalogueIcon,
  productCatalogueLabel,
  bookingUrlIcon,
  bookingUrlLabel,
  emailAddressIcon,
  emailAddressLabel,
  phoneNumberIcon,
  phoneNumberLabel,
  weChatIcon,
  weChatLabel,
}) => {
  const MIN_ITEMS_PER_PAGE = 30
  const theme = useTheme()
  const initialBrands = useMemo(() => {
    return brands.filter((brand) => {
      if (services && services.length > 0) {
        return !!brand.services
      }

      return true
    })
  }, [brands, services])

  const [currentCategory, setCurrentCategory] = useState('ALL')
  const [currentService, setCurrentService] = useState('ALL')
  const groupdBrands = arrayUtils.groupArrayObjects(
    initialBrands,
    currentCategory
  )
  const [currentLetter, setCurrentLetter] = useState('ALL')
  const [filteredBrands, setFilteredBrands] = useState(groupdBrands)
  const [categories, setCategories] = useState(
    arrayUtils.getBrandCategories(initialBrands)
  )
  const [brandsToShow, setBrandsToShow] = useState(
    Object.keys(filteredBrands).slice(0, MIN_ITEMS_PER_PAGE)
  )
  const [pagesShowing, setPagesShowing] = useState(1)
  const [serviceQueryApplied, setServiceQueryApplied] = useState(false)

  const contactIcons = useMemo(() => {
    return {
      whatsappIcon: ((whatsappIcon || {}).code || {}).code,
      productCatalogueIcon: ((productCatalogueIcon || {}).code || {}).code,
      bookingUrlIcon: ((bookingUrlIcon || {}).code || {}).code,
      emailAddressIcon: ((emailAddressIcon || {}).code || {}).code,
      phoneNumberIcon: ((phoneNumberIcon || {}).code || {}).code,
      weChatIcon: ((weChatIcon || {}).code || {}).code,
      whatsappLabel,
      emailAddressLabel,
      productCatalogueLabel,
      phoneNumberLabel,
      bookingUrlLabel,
      weChatLabel,
    }
  }, [
    whatsappIcon,
    productCatalogueIcon,
    bookingUrlIcon,
    emailAddressIcon,
    phoneNumberIcon,
    weChatIcon,
    whatsappLabel,
    emailAddressLabel,
    productCatalogueLabel,
    phoneNumberLabel,
    bookingUrlLabel,
    weChatLabel,
  ])

  const {
    location: { pathname, search },
  } = history

  const trackVirtualShopping = (action, label) => {
    if (services) {
      trackGTM('Virtual Shopping', 'Virtual Shopping', action, label)
    }
  }

  const handleCategoryChange = (e) => {
    const { value } = e.target
    const newBrands = arrayUtils.groupArrayObjects(
      initialBrands,
      value,
      currentService
    )

    if (currentLetter === 'ALL') {
      setBrandsToShow(Object.keys(newBrands).slice(0, MIN_ITEMS_PER_PAGE))
      setCategories(arrayUtils.getBrandCategories(initialBrands))
    } else {
      setBrandsToShow([currentLetter])
      setCategories(arrayUtils.getBrandCategories(newBrands[currentLetter]))
    }

    trackVirtualShopping('brand category', value)

    setCurrentCategory(value)
    setFilteredBrands(newBrands)
    setPagesShowing(1)

    GA4('category_brand_select', {
      navigation_label: value,
      village_name: villageName,
    })
  }

  const handleShowLetter = (letter) => {
    if (letter === 'ALL' || currentLetter === letter) {
      setBrandsToShow(Object.keys(groupdBrands).slice(0, MIN_ITEMS_PER_PAGE))
      setCategories(arrayUtils.getBrandCategories(initialBrands))
      setCurrentLetter('ALL')
    } else {
      setBrandsToShow([letter])
      setCategories(arrayUtils.getBrandCategories(groupdBrands[letter]))
      setCurrentLetter(letter)
    }

    GA4('brand_directory_letter_filter', { village_name: villageName })

    setPagesShowing(1)
  }

  const handleVirtualshoppingChange = (e, updateRoute = true) => {
    const { value } = e.target
    const newBrands = arrayUtils.groupArrayObjects(
      initialBrands,
      currentCategory,
      value
    )

    if (currentLetter === 'ALL') {
      setBrandsToShow(Object.keys(newBrands).slice(0, MIN_ITEMS_PER_PAGE))
      setCategories(arrayUtils.getBrandCategories(initialBrands))
    } else {
      setBrandsToShow([currentLetter])
      setCategories(arrayUtils.getBrandCategories(newBrands[currentLetter]))
    }

    if (updateRoute) {
      window.history.pushState({}, '', `?service=${encodeURIComponent(value)}`)
    }

    trackVirtualShopping('brand  services', value)

    setCurrentService(value)
    setFilteredBrands(newBrands)
    setPagesShowing(1)
  }

  const applyServiceFromParams = useCallback(
    (service) => {
      if (
        !serviceQueryApplied &&
        service &&
        service !== 'ALL' &&
        services &&
        services.find((serviceSearch) => serviceSearch.title === service)
      ) {
        const value = {
          target: {
            value: service,
          },
        }
        handleVirtualshoppingChange(value, false)
        setServiceQueryApplied(true)
      }
    },
    [services, handleVirtualshoppingChange]
  )

  useEffect(() => {
    const searchParams = parse(search)
    applyServiceFromParams(searchParams.service)
  }, [services])

  const showNextPage = () => {
    const newPageCount = pagesShowing + 1

    setBrandsToShow(
      Object.keys(filteredBrands).slice(0, newPageCount * MIN_ITEMS_PER_PAGE)
    )
    setPagesShowing(newPageCount)
  }

  const onSearchButtonClick = useCallback((e) => {
    e.preventDefault()
    toggleBrandSearch()
    trackGTM('brands', 'brands', 'brand search box secondary', pathname)
    GA4('brands_directory_inpage_search', { village_name: villageName })
  }, [])

  return (
    <Container p={`0 ${theme.space[4]}`}>
      <SectionHeader title={header} content={bodyCopy} />
      <Section textAlign="center">
        <SearchButton level="secondary" onClick={onSearchButtonClick} fixedSize>
          {ctaLabel}
          <SearchButtonIcon width={[10]} height={[10]}>
            <ChevronIcon color="currentColor" width={[10]} height={[10]} />
          </SearchButtonIcon>
        </SearchButton>
        <Select fixedSize>
          <select onChange={handleCategoryChange}>
            <option value="ALL">{categorySearchLabel}</option>
            {Object.keys(categories).map((key) => (
              <option key={key} value={key}>
                {key}
              </option>
            ))}
          </select>
          <SelectIcon width="15px" height="15px">
            <ChevronIcon width={[15]} height={[15]} />
          </SelectIcon>
        </Select>

        {services && (
          <Select
            fixedSize
            onClick={() =>
              trackGTM(
                'Virtual Shopping',
                'Virtual Shopping',
                'brand services',
                'virtual shopping services'
              )
            }>
            <select
              value={currentService}
              onChange={handleVirtualshoppingChange}>
              <option value="ALL">{servicesDropdownLabel}</option>
              {services.map((item) => (
                <option key={item.title} value={item.title}>
                  {item.title}
                </option>
              ))}
            </select>
            <SelectIcon width="15px" height="15px">
              <ChevronIcon color="currentColor" width={[15]} height={[15]} />
            </SelectIcon>
          </Select>
        )}
        <Spacing50 />
      </Section>
      <Section>
        <QuickNav
          currentLetter={currentLetter}
          entries={Object.keys(filteredBrands)}
          filteredBrands={filteredBrands}
          handleShowLetter={handleShowLetter}
        />
        <Spacing50 />
      </Section>
      {brandsToShow &&
        brandsToShow.map((key, index) => {
          if (!filteredBrands[key] || filteredBrands[key].length < 1)
            return null
          let resultsCountEl = null
          const resultsCount =
            currentLetter !== 'ALL'
              ? filteredBrands[currentLetter].length
              : Object.values(filteredBrands).reduce(
                  (acc, item) => item.length + acc,
                  0
                )

          const resultCountString =
            resultsCount === 1
              ? `${resultsCount} ${resultLabel}`
              : `${resultsCount} ${resultsLabel}`

          if (
            index === 0 &&
            (currentLetter !== 'ALL' ||
              currentCategory !== 'ALL' ||
              currentService !== 'ALL')
          ) {
            resultsCountEl = (
              <ResultCount>
                <span dir="ltr">{resultCountString.toUpperCase()}</span>
              </ResultCount>
            )
          }

          return (
            <Section key={key}>
              <Body12
                as="h3"
                fontWeight="normal"
                mt={[0]}
                mb={[7]}
                color={theme.colors.sagedarkToblack}>
                <ResultCountWrapper>
                  <div>{key}</div>
                  <div>{resultsCountEl}</div>
                </ResultCountWrapper>
              </Body12>
              <BrandList
                brands={filteredBrands[key]}
                villageName={villageName}
                contactIcons={contactIcons}
                showContactMethods={services && services.length > 0}
                visitBoutiqueLabel={visitBoutiqueLabel}
                headline={header}
              />
              <Spacing50 />
            </Section>
          )
        })}
      {pagesShowing < Object.keys(filteredBrands).length / MIN_ITEMS_PER_PAGE &&
        currentLetter === 'ALL' && (
          <Section textAlign="center">
            <Button onClick={showNextPage} fixedSize>
              {loadMoreCTACopy}
            </Button>
          </Section>
        )}
      <Spacing50 />
    </Container>
  )
}

BrandDirectory.defaultProps = {
  villageName: '',
  services: null,
  servicesDropdownLabel: '',
  whatsappIcon: { code: { code: '' } },
  whatsappLabel: '',
  productCatalogueIcon: { code: { code: '' } },
  productCatalogueLabel: '',
  bookingUrlIcon: { code: { code: '' } },
  bookingUrlLabel: '',
  emailAddressIcon: { code: { code: '' } },
  emailAddressLabel: '',
  phoneNumberIcon: { code: { code: '' } },
  phoneNumberLabel: '',
  weChatIcon: { code: { code: '' } },
  weChatLabel: '',
}

BrandDirectory.propTypes = {
  bodyCopy: PropTypes.string.isRequired,
  brands: PropTypes.arrayOf(
    PropTypes.shape({
      link: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  villageName: PropTypes.string,
  services: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.shape({
        code: PropTypes.shape({
          code: PropTypes.string.isRequired,
        }),
      }),
      label: PropTypes.string.isRequired,
    })
  ),
  header: PropTypes.string.isRequired,
  loadMoreCTACopy: PropTypes.string.isRequired,
  resultLabel: PropTypes.string.isRequired,
  resultsLabel: PropTypes.string.isRequired,
  categorySearchLabel: PropTypes.string.isRequired,
  ctaLabel: PropTypes.string.isRequired,
  visitBoutiqueLabel: PropTypes.string.isRequired,
  servicesDropdownLabel: PropTypes.string,
  toggleBrandSearch: PropTypes.func.isRequired,
  whatsappIcon: PropTypes.shape({
    code: PropTypes.shape({
      code: PropTypes.string.isRequired,
    }),
  }),
  whatsappLabel: PropTypes.string,
  productCatalogueIcon: PropTypes.shape({
    code: PropTypes.shape({
      code: PropTypes.string.isRequired,
    }),
  }),
  productCatalogueLabel: PropTypes.string,
  bookingUrlIcon: PropTypes.shape({
    code: PropTypes.shape({
      code: PropTypes.string.isRequired,
    }),
  }),
  bookingUrlLabel: PropTypes.string,
  emailAddressIcon: PropTypes.shape({
    code: PropTypes.shape({
      code: PropTypes.string.isRequired,
    }),
  }),
  emailAddressLabel: PropTypes.string,
  phoneNumberIcon: PropTypes.shape({
    code: PropTypes.shape({
      code: PropTypes.string.isRequired,
    }),
  }),
  phoneNumberLabel: PropTypes.string,
  weChatIcon: PropTypes.shape({
    code: PropTypes.shape({
      code: PropTypes.string.isRequired,
    }),
  }),
  weChatLabel: PropTypes.string,
}

export default BrandDirectory
